import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Internal Components
import Rating from './rating';
import BookingCompletionUI from './bookingcompletionUI';

/**
 * Professional Naira Formatter Utility
 */
const formatNaira = (amount) => {
  if (amount === undefined || amount === null) return "₦0.00";
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
  }).format(amount).replace('NGN', '₦');
};

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function BookingHistory() {
  const [bookings, setBookings] = useState([]);
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRating, setShowRating] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();

  // 1. Fetch User Data for Credits/Rewards Display
  useEffect(() => {
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    getDoc(userRef).then(snap => {
      if (snap.exists()) setUserData(snap.data());
    });
  }, [user]);

  // 2. Real-time Booking Listener
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'bookings'),
      where('clientId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snapshot) => {
      setBookings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error("History Listener Error:", error);
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  // Calculate Lifetime Spend (Confirmed, Paid, or Completed)
  const totalSpent = bookings
    .filter(b => b.status === 'completed' || b.status === 'confirmed' || b.status === 'paid')
    .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

  /**
   * Professional PDF Statement Export
   */
  const exportPDF = () => {
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(22);
    doc.setTextColor(0, 31, 63); // Navy
    doc.text('ESDRAS', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text('Grooming & AI Scan Rewards Statement', 105, 27, { align: 'center' });

    // Summary Box in PDF
    doc.setDrawColor(184, 134, 11); // Gold
    doc.rect(14, 35, 182, 25);
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text(`User: ${user.displayName || user.email}`, 20, 42);
    doc.text(`Total Spend: ${formatNaira(totalSpent)}`, 20, 52);
    
    doc.setTextColor(184, 134, 11);
    doc.text(`Free AI Scans Earned: ${userData.credits || 0}`, 120, 48);

    // Table Mapping
    const tableRows = bookings.map(b => [
      b.createdAt ? format(b.createdAt.toDate(), 'yyyy-MM-dd') : '...',
      b.stylistName || 'Stylist',
      b.styleName || 'Custom',
      formatNaira(b.amount),
      b.status?.toUpperCase()
    ]);

    doc.autoTable({
      startY: 65, 
      head: [['Date', 'Stylist', 'Style', 'Amount', 'Status']], 
      body: tableRows,
      headStyles: { fillColor: [184, 134, 11], textColor: [0, 0, 0] }, // Gold head
      alternateRowStyles: { fillColor: [245, 245, 245] }
    });

    doc.save(`Esdras_Statement_${user.uid.slice(0, 5)}.pdf`);
  };

  if (!user && !loading) {
    return (
      <div style={fullPageCenter}>
        <h2 style={{ color: GOLD }}>Access Denied</h2>
        <p>Please log in to view your grooming history.</p>
        <button onClick={() => navigate('/login')} style={goldBtnSmall}>Login</button>
      </div>
    );
  }

  return (
    <div style={mainContainer}>
      
      {/* Header Area */}
      <div style={headerArea}>
        <img 
          src="/icons/esdras-logo.png" 
          alt="Esdras" 
          style={logoStyle} 
          onClick={() => navigate('/dashboard')} 
        />
        <h1 style={pageTitle}>History</h1>
      </div>

      {/* Summary Card: Spend & Credits */}
      {!loading && (
        <div style={summaryCard}>
          <div style={summaryFlex}>
            <div style={summaryItem}>
              <p style={summaryLabel}>Lifetime Spend</p>
              <h2 style={summaryValue}>{formatNaira(totalSpent)}</h2>
            </div>
            <div style={divider}></div>
            <div style={summaryItem}>
              <p style={summaryLabel}>AI Scan Rewards</p>
              <h2 style={{ ...summaryValue, color: GOLD }}>{userData.credits || 0} Credits</h2>
            </div>
          </div>
          <button onClick={exportPDF} style={pdfBtn}>📥 DOWNLOAD STATEMENT</button>
        </div>
      )}

      {/* Main Booking List */}
      {loading ? (
        <div style={loaderContainer}><div className="spinner"></div></div>
      ) : (
        <div style={listWrapper}>
          {bookings.length === 0 ? (
            <div style={emptyState}>
               <p style={{ opacity: 0.5 }}>No booking history found.</p>
               <button onClick={() => navigate('/styles')} style={goldBtnSmall}>Browse Styles</button>
            </div>
          ) : (
            bookings.map((booking) => (
              <div key={booking.id} style={bookingCard}>
                 <div style={cardTopRow}>
                  <div style={cardLeft}>
                     <img 
                        src={booking.styleImage || '/icons/default-hair.png'} 
                        style={thumbStyle} 
                        alt="style" 
                      />
                     <div>
                        <h3 style={stylistName}>{booking.stylistName}</h3>
                        <p style={styleNameLabel}>{booking.styleName}</p>
                        <p style={dateLabel}>
                          {booking.createdAt ? format(booking.createdAt.toDate(), 'MMM d, yyyy') : 'Pending...'}
                        </p>
                     </div>
                  </div>
                  <div style={cardRight}>
                    <p style={amountLabel}>{formatNaira(booking.amount)}</p>
                    <p style={{ 
                      ...statusBadge,
                      color: (booking.status === 'confirmed' || booking.status === 'completed') ? '#2ecc71' : GOLD 
                    }}>
                      {booking.status?.toUpperCase()}
                    </p>
                  </div>
                </div>

                {/* Escrow/Completion Controls: Only for Paid/Confirmed sessions */}
                {(booking.status === 'paid' || booking.status === 'confirmed') && (
                  <div style={controlSection}>
                    <BookingCompletionUI booking={booking} />
                  </div>
                )}

                {/* Completed State: Show Rating Trigger */}
                {booking.status === 'completed' && (
                  <div style={completedActionArea}>
                     <span style={finalizedLabel}>✓ Session Finalized & Paid</span>
                     <button onClick={() => setShowRating(booking.id)} style={rateLinkBtn}>Rate Session</button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Rating Modal Overlays */}
      {showRating && (
        <Rating
          bookingId={showRating}
          stylistId={bookings.find(b => b.id === showRating)?.barberId || bookings.find(b => b.id === showRating)?.stylistId}
          onClose={() => setShowRating(null)}
        />
      )}
    </div>
  );
}

// --- STYLES OBJECT ---

const mainContainer = { 
  minHeight: '100vh', 
  background: '#0a0a0a', 
  color: 'white', 
  fontFamily: 'Montserrat, sans-serif', 
  padding: '1rem 1rem 4rem' 
};

const headerArea = { 
  textAlign: 'center', 
  marginBottom: '1.5rem', 
  paddingTop: '1rem' 
};

const logoStyle = { 
  width: '70px', 
  cursor: 'pointer', 
  marginBottom: '10px' 
};

const pageTitle = { 
  color: GOLD, 
  fontSize: '2rem', 
  fontWeight: '800', 
  marginTop: '0px',
  letterSpacing: '1px'
};

const summaryCard = { 
  maxWidth: '700px', 
  margin: '0 auto 2rem', 
  background: 'linear-gradient(135deg, #001F3F 0%, #000 100%)', 
  padding: '1.5rem', 
  borderRadius: '24px', 
  border: `1px solid ${GOLD}`, 
  textAlign: 'center',
  boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
};

const summaryFlex = { 
  display: 'flex', 
  justifyContent: 'space-around', 
  alignItems: 'center' 
};

const summaryItem = { flex: 1 };

const summaryLabel = { 
  margin: 0, 
  opacity: 0.7, 
  fontSize: '0.7rem', 
  textTransform: 'uppercase',
  letterSpacing: '1px'
};

const summaryValue = { 
  fontSize: '1.6rem', 
  color: 'white', 
  margin: '5px 0 0' 
};

const divider = { 
  width: '1px', 
  height: '40px', 
  background: 'rgba(255,255,255,0.1)' 
};

const listWrapper = { 
  maxWidth: '700px', 
  margin: '0 auto' 
};

const bookingCard = { 
  background: 'rgba(255,255,255,0.03)', 
  padding: '1.2rem', 
  borderRadius: '20px', 
  marginBottom: '1rem', 
  border: '1px solid rgba(255,255,255,0.05)',
  transition: 'transform 0.2s'
};

const cardTopRow = { 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center' 
};

const cardLeft = { 
  display: 'flex', 
  gap: '15px', 
  alignItems: 'center' 
};

const cardRight = { textAlign: 'right' };

const thumbStyle = { 
  width: '55px', 
  height: '55px', 
  borderRadius: '12px', 
  objectFit: 'cover', 
  border: `1px solid rgba(184, 134, 11, 0.4)` 
};

const stylistName = { 
  margin: 0, 
  fontSize: '1.1rem', 
  color: GOLD 
};

const styleNameLabel = { 
  margin: '2px 0', 
  fontSize: '0.85rem' 
};

const dateLabel = { 
  margin: 0, 
  fontSize: '0.75rem', 
  opacity: 0.5 
};

const amountLabel = { 
  fontSize: '1.1rem', 
  fontWeight: 'bold', 
  margin: 0 
};

const statusBadge = { 
  fontSize: '0.65rem', 
  margin: '5px 0',
  fontWeight: 'bold',
  letterSpacing: '0.5px'
};

const controlSection = { marginTop: '15px' };

const completedActionArea = { 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center', 
  marginTop: '15px', 
  paddingTop: '10px', 
  borderTop: '1px solid rgba(255,255,255,0.08)' 
};

const finalizedLabel = { 
  fontSize: '0.7rem', 
  color: '#2ecc71' 
};

const pdfBtn = { 
  marginTop: '20px', 
  background: GOLD, 
  color: 'black', 
  border: 'none', 
  padding: '12px 25px', 
  borderRadius: '50px', 
  fontWeight: '900', 
  cursor: 'pointer', 
  fontSize: '0.75rem', 
  width: '100%', 
  maxWidth: '280px',
  letterSpacing: '1px'
};

const goldBtnSmall = { 
  background: GOLD, 
  color: 'black', 
  border: 'none', 
  padding: '10px 20px', 
  borderRadius: '50px', 
  fontWeight: 'bold', 
  marginTop: '15px', 
  cursor: 'pointer' 
};

const rateLinkBtn = { 
  background: 'none', 
  border: 'none', 
  color: GOLD, 
  fontSize: '0.75rem', 
  cursor: 'pointer', 
  textDecoration: 'underline',
  fontWeight: 'bold'
};

const emptyState = { 
  textAlign: 'center', 
  marginTop: '3rem' 
};

const fullPageCenter = { 
  minHeight: '100vh', 
  background: '#001F3F', 
  display: 'grid', 
  placeItems: 'center', 
  color: GOLD,
  textAlign: 'center',
  padding: '20px'
};

const loaderContainer = { 
  display: 'flex', 
  justifyContent: 'center', 
  padding: '3rem' 
};
    
